import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Card, CardContent, Typography, Box, Stack, Snackbar, Alert, Dialog, DialogContent, IconButton, RadioGroup, FormControlLabel, Radio, CircularProgress } from '@mui/material';
import { HotTable } from '@handsontable/react';
import Handsontable from 'handsontable';
import 'handsontable/dist/handsontable.full.min.css';
import { socket } from '../socket';
import { debounce } from 'lodash';
import CloseIcon from '@mui/icons-material/Close';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';

// 핸즈온테이블 스크롤바 스타일링
const tableStyles = `
  /* 테이블 컨테이너 스타일 */
  .handsontable {
    font-size: 14px !important;
    font-family: 'Roboto', sans-serif !important;
    overflow: hidden !important;
  }
  
  /* 메인 스크롤 영역 - 오른쪽 스크롤바만 표시 */
  .handsontable .wtHolder {
    overflow: auto !important;
    scrollbar-width: thin !important;
    scrollbar-color: #c1c1c1 #f1f1f1 !important;
  }
  
  /* 웹킷 브라우저 스크롤바 스타일 */
  .handsontable .wtHolder::-webkit-scrollbar {
    width: 16px !important;
    height: 16px !important;
  }
  
  .handsontable .wtHolder::-webkit-scrollbar-track {
    background: #e0e0e0 !important;
    border-radius: 8px !important;
  }
  
  .handsontable .wtHolder::-webkit-scrollbar-thumb {
    background: #999 !important;
    border-radius: 8px !important;
    border: 3px solid #e0e0e0 !important;
  }
  
  .handsontable .wtHolder::-webkit-scrollbar-thumb:hover {
    background: #666 !important;
  }
  
  .handsontable .wtHolder::-webkit-scrollbar-corner {
    background: #e0e0e0 !important;
  }

  /* 왼쪽 행 헤더 스크롤 숨기기 */
  .handsontable .ht_clone_left .wtHolder {
    overflow: hidden !important;
  }
  
  .handsontable .ht_clone_left .wtHolder::-webkit-scrollbar {
    display: none !important;
  }
  
  /* 상단 헤더 스크롤 숨기기 */
  .handsontable .ht_clone_top .wtHolder {
    overflow: hidden !important;
  }
  
  .handsontable .ht_clone_top .wtHolder::-webkit-scrollbar {
    display: none !important;
  }
  
  /* 상단 왼쪽 코너 스크롤 숨기기 */
  .handsontable .ht_clone_top_left_corner .wtHolder {
    overflow: hidden !important;
  }
  
  .handsontable .ht_clone_top_left_corner .wtHolder::-webkit-scrollbar {
    display: none !important;
  }
  
  /* 테이블 셀 스타일 */
  .handsontable .htCenter {
    text-align: center !important;
  }
  
  .handsontable .htMiddle {
    vertical-align: middle !important;
  }
  
     .handsontable .htCustomerMessage {
     background-color: #fff9c4 !important;
     color: #333333 !important;
     font-weight: 700 !important;
   }
  
  .handsontable .htContactCell {
    color: #1976d2 !important;
    text-decoration: underline !important;
    font-weight: 600 !important;
    cursor: pointer !important;
  }
  
  .handsontable td {
    border: 1px solid #e0e0e0 !important;
  }
  
  .handsontable th {
    background-color: #f5f5f5 !important;
    border: 1px solid #e0e0e0 !important;
    font-weight: 600 !important;
  }
`;

interface RowData {
  vehicleNumber: string;
  route: string;
  contact: string;
  driverName: string;
  date?: string;
  customerMessage?: string;
  company: string;
  cargo?: string;
}

interface Props {
  rows: RowData[];
  loading: boolean;
  isMobile: boolean;
  fetchData: () => void;
  dateRange: { start: string; end: string };
  currentCompany?: string;
  customerInfo?: { name: string };
  customerId?: string;
  viewType?: 'individual' | 'summary'; // 외부에서 제어
  hasQueried?: boolean;
}

export function OttogiViewTypeSelector({ viewType, setViewType }: { viewType: 'individual' | 'summary', setViewType: (v: 'individual' | 'summary') => void }) {
  return (
    <Box sx={{ 
      py: 0.5, 
      px: 2, 
      bgcolor: '#fff', 
      borderBottom: '1px solid #e0e0e0', 
      display: 'flex', 
      justifyContent: 'center',
      minHeight: 'auto'
    }}>
      <RadioGroup 
        row 
        value={viewType} 
        onChange={e => setViewType(e.target.value as 'individual' | 'summary')}
        sx={{ 
          '& .MuiRadioGroup-root': { 
            minHeight: 'auto' 
          }
        }}
      >
        <FormControlLabel 
          value="individual" 
          control={<Radio size="small" sx={{ py: 0.5 }} />} 
          label="개별" 
          sx={{ 
            mr: 2.5, 
            my: 0,
            '& .MuiFormControlLabel-label': { 
              fontSize: 13,
              fontWeight: 500,
              py: 0
            }
          }} 
        />
        <FormControlLabel 
          value="summary" 
          control={<Radio size="small" sx={{ py: 0.5 }} />} 
          label="요약" 
          sx={{ 
            my: 0,
            '& .MuiFormControlLabel-label': { 
              fontSize: 13,
              fontWeight: 500,
              py: 0
            }
          }} 
        />
      </RadioGroup>
    </Box>
  );
}

const CustomerDispatchReportViewer: React.FC<Props> = ({ rows, loading, isMobile, fetchData, dateRange, currentCompany, customerInfo, customerId, viewType = 'individual', hasQueried = false }) => {
  const isOttogi = customerId === 'otoki_ramen' || currentCompany === '오뚜기농협물류' || customerInfo?.name === '오뚜기라면';
  const viewMode = isMobile ? 'card' : 'table';

  // summaryData 상태 추가
  const [summaryData, setSummaryData] = useState<{ exportData: Array<{ export: number, number: string }>, domesticData: Array<{ domestic: number, shipment: number, number: string }> }>({ exportData: [], domesticData: [] });
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  // summaryData fetch 함수
  const fetchSummaryData = useCallback(async () => {
    if (!isOttogi || !dateRange.start) return;
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const res = await fetch(`/api/dispatch-reports/${dateRange.start}/summary`);
      if (!res.ok) throw new Error('데이터 없음');
      const data = await res.json();
      setSummaryData({
        exportData: Array.isArray(data.exportData) ? data.exportData : [],
        domesticData: Array.isArray(data.domesticData) ? data.domesticData : []
      });
    } catch (e: any) {
      setSummaryData({ exportData: [], domesticData: [] });
      setSummaryError('수출/내수 요약 데이터를 불러올 수 없습니다.');
    } finally {
      setSummaryLoading(false);
    }
  }, [isOttogi, dateRange.start]);

  // 날짜 변경 시 summaryData fetch
  useEffect(() => {
    if (isOttogi && viewType === 'summary') {
      fetchSummaryData();
    }
  }, [isOttogi, viewType, dateRange.start, fetchSummaryData]);

  // 배차완료 알림 상태
  const [dispatchNotification, setDispatchNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'info' | 'warning' | 'error';
    route?: string;
    vehicleNumber?: string;
    company?: string;
  }>({
    open: false,
    message: '',
    severity: 'success',
    route: '',
    vehicleNumber: '',
    company: ''
  });

  // 탭 타이틀 깜빡임 관련
  const originalTitle = useRef(document.title);
  const blinkInterval = useRef<any>(null);
  const originalFavicon = useRef<string | null>(null);
  const hotTableRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // 스타일 태그 추가
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = tableStyles + `
      /* 모바일 배차 화면 스크롤바 숨김 및 터치 제한 */
      ${isMobile ? `
        .dispatch-container {
          touch-action: pan-y !important;
          -webkit-overflow-scrolling: touch !important;
          overscroll-behavior: contain !important;
        }
        .dispatch-card-container {
          scrollbar-width: none !important;
          -ms-overflow-style: none !important;
          touch-action: pan-y !important;
          -webkit-overflow-scrolling: touch !important;
          overscroll-behavior: contain !important;
        }
        .dispatch-card-container::-webkit-scrollbar {
          display: none !important;
        }
      ` : ''}
    `;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, [isMobile, rows?.length]);

  function setFavicon(url: string) {
    let link: HTMLLinkElement | null = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = url;
  }

  function startBlinking(title: string) {
    if (blinkInterval.current) return;
    let visible = false;
    let count = 0;
    if (!originalFavicon.current) {
      const link: HTMLLinkElement | null = document.querySelector("link[rel~='icon']");
      originalFavicon.current = link?.href || '/favicon.ico';
    }
    blinkInterval.current = setInterval(() => {
      document.title = visible
        ? '🚨🚚🚨 배차완료 알림! 🚨🚚🚨'
        : originalTitle.current;
      setFavicon(visible
        ? '/alert-favicon.ico'
        : originalFavicon.current || '/favicon.ico');
      visible = !visible;
      count++;
      if (count > 60) stopBlinking(); // 약 18초 후 자동 종료
    }, 300);
  }

  function stopBlinking() {
    if (blinkInterval.current) {
      clearInterval(blinkInterval.current);
      blinkInterval.current = null;
      document.title = originalTitle.current;
      setFavicon(originalFavicon.current || '/favicon.ico');
    }
  }

  // 소켓 이벤트 리스너 등록 (현재 보고 있는 날짜 범위에 포함될 때만 알림)
  useEffect(() => {
    const handleDispatchAssigned = (data: {
      date: string;
      company: string;
      route: string;
      vehicleNumber: string;
      driverName: string;
      message: string;
      eventId: string;
    }) => {
      console.log('[CustomerDispatchReportViewer] dispatch-assigned 이벤트 수신:', {
        data,
        currentCompany,
        dateRange,
        isInDateRange: data.date >= dateRange.start && data.date <= dateRange.end,
        isCurrentCompany: data.company === currentCompany
      });
      
      // 현재 화주가 보고 있는 날짜 범위에 포함되는지 체크
      const notificationDate = data.date;
      const isInDateRange = notificationDate >= dateRange.start && notificationDate <= dateRange.end;
      
      // 🔑 회사 정보가 현재 화주와 일치하는지 확인
      const isCurrentCompany = data.company === currentCompany;
      
             if (isInDateRange && isCurrentCompany) {
         console.log('[CustomerDispatchReportViewer] 배차완료 알림 표시:', {
           company: data.company,
           route: data.route,
           vehicleNumber: data.vehicleNumber,
           fullData: data
         });
         
         // rows 데이터에서 해당 날짜의 배차 정보 찾기
                   const matchingRow = rows.find(row => 
           row.date === data.date && 
           row.company === data.company
         );
         
         console.log('[CustomerDispatchReportViewer] 매칭된 row 데이터:', matchingRow);
         
         setDispatchNotification({
           open: true,
           message: data.message,
           severity: 'success',
           route: matchingRow?.route || data.route || '데이터 없음',
           vehicleNumber: matchingRow?.vehicleNumber || data.vehicleNumber || '데이터 없음',
           company: data.company
         });
         startBlinking('🚚 배차완료 알림!');
       } else {
        console.log('[CustomerDispatchReportViewer] 배차완료 알림 조건 불충족:', {
          isInDateRange,
          isCurrentCompany,
          reason: !isInDateRange ? '날짜 범위 불일치' : '회사 불일치'
        });
      }
    };
    socket.on('dispatch-assigned', handleDispatchAssigned);
    // 창에 포커스가 오면 깜빡임 멈춤
    window.addEventListener('focus', stopBlinking);
    return () => {
      socket.off('dispatch-assigned', handleDispatchAssigned);
      window.removeEventListener('focus', stopBlinking);
      stopBlinking();
    };
  }, [dateRange, currentCompany]); // 🔑 currentCompany 의존성 추가

  // 디바운싱된 fetchData 함수 생성
  const debouncedFetchData = useCallback(
    debounce(() => {
      if (typeof fetchData === 'function') {
        fetchData();
      }
    }, 300), // 300ms 지연
    [fetchData]
  );

  // 소켓을 통한 실시간 데이터 동기화 (화주별 필터링 적용)
  useEffect(() => {
    const handleReportUpdated = (data: { date: string, company?: string, eventId?: string, userId?: string }) => {
      console.log('[CustomerDispatchReportViewer] report-updated 이벤트 수신:', {
        data,
        currentCompany,
        dateRange,
        isMobile,
        viewMode,
        isInDateRange: data.date >= dateRange.start && data.date <= dateRange.end,
        isCurrentCompany: !data.company || !currentCompany || data.company === currentCompany
      });
      
      // 현재 화주가 보고 있는 날짜 범위에 포함되는지 체크
      const updateDate = data.date;
      const isInDateRange = updateDate >= dateRange.start && updateDate <= dateRange.end;
      
      // 회사 정보가 있는 경우 현재 화주와 일치하는지 확인
      const isCurrentCompany = !data.company || !currentCompany || data.company === currentCompany;
      
      if (isInDateRange && isCurrentCompany) {
        console.log('[CustomerDispatchReportViewer] 실시간 업데이트 실행 (모바일:', isMobile, ')');
        debouncedFetchData();
      } else {
        console.log('[CustomerDispatchReportViewer] 실시간 업데이트 조건 불충족:', {
          isInDateRange,
          isCurrentCompany,
          isMobile,
          reason: !isInDateRange ? '날짜 범위 불일치' : '회사 불일치'
        });
      }
    };

    console.log('[CustomerDispatchReportViewer] 소켓 리스너 등록 (모바일:', isMobile, ')');
    socket.on('report-updated', handleReportUpdated);
    
    return () => {
      console.log('[CustomerDispatchReportViewer] 소켓 리스너 해제 (모바일:', isMobile, ')');
      socket.off('report-updated', handleReportUpdated);
      // 컴포넌트 언마운트 시 대기 중인 디바운싱 취소
      debouncedFetchData.cancel();
    };
  }, [debouncedFetchData, dateRange, currentCompany, isMobile]);

  const handleCloseNotification = () => {
    setDispatchNotification(prev => ({ 
      ...prev, 
      open: false,
      route: '',
      vehicleNumber: '',
      company: ''
    }));
    stopBlinking();
  };

  // 핸즈온테이블용 데이터 변환
  const getTableData = () => {
    if (!rows || rows.length === 0) {
      return [];
    }
    
    // route가 있는 데이터만 필터링
    const filteredRows = rows.filter((row: any) => {
      return row?.route;
    });
    
    const tableData = filteredRows.map((row: any) => {
      return [
        row?.date || '-',
        row?.route || '-',
        row?.vehicleNumber || '-',
        row?.cargo || '-',
        row?.contact || '-',
        row?.driverName || '-',
        row?.customerMessage || '-'
      ];
    });
    
    return tableData;
  };

  // route(운송구간)이 하나라도 있는지 여부
  const hasRouteData = rows && rows.length > 0 && rows.some(row => !!row.route);
  
  // 데이터 없음 메시지는 실제 조회(검색)가 수행된 이후에만 표시
  // hasQueried: 상위에서 조회 버튼/자동 조회 시 true로 설정됨
  const shouldShowNoData = !!hasQueried && !loading && !hasRouteData;

  // 핸즈온테이블 컬럼 헤더
  const tableHeaders = ['날짜', '운송구간', '차량번호', '차종', '연락처', '이름', '메시지'];
  const tableColWidths = [120, 400, 120, 100, 120, 100, 400]; // 컬럼 너비 조정하여 스크롤 유도

  // 핸즈온테이블 셀 스타일 설정
  const tableCells = (row: number, col: number) => {
    const cellProps: any = {
      readOnly: true // 모든 셀 읽기 전용
    };

         // 메시지 컬럼(인덱스 6) 스타일
     if (col === 6) { // 메시지 컬럼 
       cellProps.className = 'htCustomerMessage htCenter htMiddle';
       cellProps.style = { 
         backgroundColor: '#fff9c4', 
         color: '#333333', 
         fontWeight: 700 
       };
     } else if (col === 4 && isMobile) { // 연락처 컬럼 (모바일에서만 링크 스타일)
      cellProps.className = 'htContactCell htCenter htMiddle';
      cellProps.style = { 
        color: '#1976d2', 
        textDecoration: 'underline',
        fontWeight: 600,
        cursor: 'pointer'
      };
    } else {
      cellProps.className = 'htCenter htMiddle';
    }

    return cellProps;
  };

  // 연락처 셀 클릭 핸들러 (모바일에서만 동작)
  const handleCellClick = (event: any, coords: any) => {
    const { row, col } = coords;
    if (isMobile && col === 4) { // 모바일에서만 연락처 컬럼 클릭 가능
      // 필터링된 데이터에서 해당 행의 연락처 정보 가져오기
              const filteredRows = rows.filter((r: any) => r?.route);
      if (filteredRows[row]?.contact) {
        const phoneNumber = (filteredRows[row].contact || '').replace(/[^0-9]/g, '');
        if (phoneNumber) {
          window.open(`tel:${phoneNumber}`, '_self');
        }
      }
    }
  };

  // 테이블 리사이즈 핸들러
  const handleResize = useCallback(() => {
    try {
      if (hotTableRef.current && hotTableRef.current.hotInstance) {
        setTimeout(() => {
          if (hotTableRef.current && hotTableRef.current.hotInstance) {
            hotTableRef.current.hotInstance.render();
          }
        }, 100);
      }
    } catch (error) {
      console.warn('핸즈온테이블 리사이즈 에러:', error);
    }
  }, []);

  // 데이터 변경 시 HotTable 업데이트
  useEffect(() => {
    if (viewMode !== 'table') return;
    
    if (hotTableRef.current && hotTableRef.current.hotInstance) {
      const hotInstance = hotTableRef.current.hotInstance;
      const newData = getTableData();
      
      // 데이터 로드
      hotInstance.loadData(newData);
      
      // 렌더링 강제 실행
      setTimeout(() => {
        if (hotTableRef.current && hotTableRef.current.hotInstance) {
          hotTableRef.current.hotInstance.render();
        }
      }, 100);
    }
  }, [rows, viewMode]);

  // 윈도우 리사이즈 이벤트 리스너
  useEffect(() => {
    if (viewMode !== 'table') return;
    
    const resizeHandler = () => {
      setTimeout(handleResize, 100);
    };
    
    window.addEventListener('resize', resizeHandler);
    
    // 줌 레벨 변경 감지
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey) {
        setTimeout(handleResize, 300);
      }
    };
    window.addEventListener('wheel', handleWheel, { passive: true });
    
    return () => {
      window.removeEventListener('resize', resizeHandler);
      window.removeEventListener('wheel', handleWheel);
    };
  }, [viewMode, handleResize]);

  // 표 형태 렌더링 (핸즈온테이블)
  const renderTableView = () => {
    const currentData = getTableData();
    
    return (
      <Box sx={{ 
        width: '95%',
        height: '600px', // 고정 높이로 변경하여 테스트
        flex: 1, // 부모의 남은 공간을 모두 차지
        minHeight: 0, // flex shrink 허용
        overflow: 'auto', // 표에만 스크롤
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        backgroundColor: '#fff',
        mx: 'auto'
      }}>
        <HotTable
          ref={hotTableRef}
          data={currentData}
          colHeaders={tableHeaders}
          colWidths={tableColWidths}
          rowHeaders={true}
          width="100%"
          height="580px"
          licenseKey={process.env.REACT_APP_HANDSONTABLE_LICENSE_KEY || 'non-commercial-and-evaluation'}
          stretchH="all"
          fixedRowsTop={0}
          fixedColumnsLeft={0}
          manualRowResize={false}
          manualColumnResize={true}
          autoColumnSize={false}
          autoRowSize={false}
          renderAllRows={true}
          viewportRowRenderingOffset={100}
          className="htCenter"
          cells={tableCells}
          afterOnCellMouseDown={handleCellClick}
          contextMenu={false}
          readOnly={true}
          preventOverflow="horizontal"
          outsideClickDeselects={false}
          fillHandle={false}
          disableVisualSelection={true}
          afterInit={() => {
            // HotTable 초기화 완료
          }}
          afterLoadData={() => {
            // HotTable 데이터 로드 완료
          }}
          afterRender={() => {
            // HotTable 렌더링 완료
          }}
        />
      </Box>
    );
  };

  // 카드 형태 렌더링
  const renderCardView = () => {
    // route와 vehicleNumber 둘 다 없는 데이터는 제외
    const filteredRows = rows.filter(row => row?.route || row?.vehicleNumber);
    

    
    return (
      <Box sx={{
        width: '100vw',
        maxWidth: '100vw',
        overflowX: 'hidden',
        boxSizing: 'border-box',
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: 2,
        px: 0,
        mx: 0,
        margin: 0,
        pb: isMobile ? 22 : 0, // 모바일에서 하단 네비게이션 바 높이만큼 패딩 추가 (증가)
        // 터치 스크롤 안정성 개선
        touchAction: 'pan-y',
        WebkitOverflowScrolling: 'touch',
        overscrollBehavior: 'contain',
        // 스크롤 성능 최적화
        willChange: 'scroll-position',
        transform: 'translateZ(0)'
      }}>
        {filteredRows.map((row, idx) => (
          <Box key={idx} sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: isMobile ? '0 2px 8px rgba(0, 0, 0, 0.1)' : '0 2px 12px rgba(0,0,0,0.08)', 
              bgcolor: '#fff', 
              p: 0.5, 
              mb: 2, 
              width: '100%',
              maxWidth: '100%',
              border: '1px solid #e9ecef'
            }}>
              <CardContent sx={{ p: 2.5 }}>
                <Stack direction="column" spacing={1.5} alignItems="flex-start">
                  {/* 날짜 */}
                  <Typography 
                    sx={{ 
                      fontWeight: 600, 
                      color: '#1976d2', 
                      fontSize: 16,
                      mb: 1,
                      letterSpacing: '-0.02em'
                    }}
                  >
                    {row.date ? `${row.date} (${new Date(row.date).toLocaleDateString('ko-KR', { weekday: 'short' })})` : '-'}
                  </Typography>
                  {/* 배차 정보 */}
                  <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <Typography sx={{ minWidth: 70, color: '#757575', fontWeight: 500, fontSize: 14, letterSpacing: '-0.01em' }}>
                        운송구간
                      </Typography>
                      <Typography sx={{ color: '#333', fontWeight: 600, fontSize: 15, letterSpacing: '-0.01em' }}>
                        {row.route || '-'}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <Typography sx={{ minWidth: 70, color: '#757575', fontWeight: 500, fontSize: 14, letterSpacing: '-0.01em' }}>
                        차량번호
                      </Typography>
                      <Typography sx={{ color: '#333', fontWeight: 600, fontSize: 15, letterSpacing: '-0.01em' }}>
                        {row.vehicleNumber || '-'}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <Typography sx={{ minWidth: 70, color: '#757575', fontWeight: 500, fontSize: 14, letterSpacing: '-0.01em' }}>
                        차종
                      </Typography>
                      <Typography sx={{ color: '#333', fontWeight: 600, fontSize: 15, letterSpacing: '-0.01em' }}>
                        {row.cargo || '-'}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <Typography sx={{ minWidth: 70, color: '#757575', fontWeight: 500, fontSize: 14, letterSpacing: '-0.01em' }}>
                        연락처
                      </Typography>
                      {isMobile ? (
                        <a
                          href={`tel:${(row.contact || '').replace(/[^0-9]/g, '')}`}
                          style={{ 
                            color: '#1976d2', 
                            textDecoration: 'none', 
                            fontWeight: 600, 
                            fontSize: 15,
                            letterSpacing: '-0.01em'
                          }}
                        >
                          {row.contact || '-'}
                        </a>
                      ) : (
                        <Typography sx={{ color: '#333', fontWeight: 600, fontSize: 15, letterSpacing: '-0.01em' }}>
                          {row.contact || '-'}
                        </Typography>
                      )}
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography sx={{ minWidth: 70, color: '#757575', fontWeight: 500, fontSize: 14, letterSpacing: '-0.01em' }}>
                        이름
                      </Typography>
                      <Typography sx={{ color: '#333', fontWeight: 600, fontSize: 15, letterSpacing: '-0.01em' }}>
                        {row.driverName || '-'}
                      </Typography>
                    </Stack>
                  </Box>
                  {/* 아래쪽: 화주전달 메시지 */}
                  {row.customerMessage && (
                                         <Box sx={{
                       bgcolor: '#fff9c4',
                       color: '#333333',
                       fontWeight: 600,
                       borderRadius: 2,
                       p: 1.5,
                       minWidth: 120,
                       maxWidth: '100%',
                       fontSize: 14,
                       boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                       wordBreak: 'break-all',
                       textAlign: 'center',
                       alignSelf: 'stretch',
                       display: 'flex',
                       alignItems: 'center',
                       justifyContent: 'center',
                       mt: 1,
                       letterSpacing: '-0.01em'
                     }}>
                      {row.customerMessage || '-'}
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>
    );
  };

  // 화주전달 메시지 스타일 추가
  useEffect(() => {
    if (!document.getElementById('customer-table-style')) {
      const style = document.createElement('style');
      style.id = 'customer-table-style';
      style.innerHTML = `
        .htCustomerMessage { 
          background: #fff9c4 !important; 
          color: #1976d2 !important; 
          font-weight: 700 !important; 
        }
        .htContactCell {
          color: #1976d2 !important;
          text-decoration: underline !important;
          font-weight: 600 !important;
          cursor: pointer !important;
        }
        .htContactCell:hover {
          background: #e3f2fd !important;
        }
        /* 핸즈온테이블 안정성 개선 */
        .handsontable {
          font-size: 14px !important;
          line-height: 1.4 !important;
        }
        .handsontable td {
          white-space: nowrap !important;
          overflow: hidden !important;
          text-overflow: ellipsis !important;
        }
        .handsontable .htCore {
          border-collapse: separate !important;
          border-spacing: 0 !important;
        }
        .handsontable .wtHolder {
          overflow: auto !important;
        }
        /* 터치 스크롤 안정성 개선 */
        .dispatch-container {
          -webkit-overflow-scrolling: touch !important;
          overscroll-behavior: contain !important;
          touch-action: pan-y !important;
          will-change: scroll-position !important;
          transform: translateZ(0) !important;
        }
        .dispatch-container * {
          -webkit-tap-highlight-color: transparent !important;
          -webkit-touch-callout: none !important;
          -webkit-user-select: none !important;
          user-select: none !important;
        }
        .dispatch-container .MuiCard-root {
          -webkit-tap-highlight-color: transparent !important;
          touch-action: manipulation !important;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);



  useEffect(() => {
    if (viewMode === 'table' && containerRef.current) {
      // 표형으로 전환될 때 스크롤을 최상단으로 이동
      containerRef.current.scrollTop = 0;
    }
  }, [viewMode]);

  // 수출/내수 카드 렌더링 함수 (예시)
  const renderSummaryCards = () => {
    // 수출: 0이 아닌 항목만 표시, 합계 계산
    const filteredExport = summaryData.exportData.filter(item => item.export !== 0);
    const exportSum = filteredExport.reduce((sum, item) => sum + (item.export || 0), 0);
    // 내수: domestic, shipment 모두 0이 아닌 항목만 표시, 합계 계산
    const filteredDomestic = summaryData.domesticData.filter(item => (item.domestic !== 0 || item.shipment !== 0));
    const domesticSum = filteredDomestic.reduce((sum, item) => sum + (item.domestic || 0), 0);
    const shipmentSum = filteredDomestic.reduce((sum, item) => sum + (item.shipment || 0), 0);

    return (
      <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2, mt: 2 }}>
        <Card sx={{ flex: 1, minWidth: 300 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2', mr: 1 }}>
                수출
              </Typography>
            </Box>
            {summaryLoading ? (
                              <CircularProgress size={20} sx={{ color: '#1976d2' }} />
            ) : summaryError ? (
              <Typography color="error">{summaryError}</Typography>
            ) : filteredExport.length === 0 ? (
              <Typography color="text.secondary">수출/차량번호 데이터가 없습니다.</Typography>
            ) : (
              <>
                {/* 테이블 헤더 */}
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: '80px 1fr',
                  gap: 1,
                  p: 1,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 1,
                  mb: 1,
                  border: '1px solid #e0e0e0'
                }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 14, textAlign: 'center' }}>수출</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: 14, textAlign: 'center' }}>차량번호</Typography>
                </Box>
                {/* 테이블 데이터 */}
                {filteredExport.map((item, idx) => (
                  <Box key={idx} sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: '80px 1fr',
                    gap: 1,
                    p: 1,
                    borderBottom: idx === filteredExport.length - 1 ? 'none' : '1px solid #e0e0e0',
                    '&:hover': { backgroundColor: '#f8f9fa' }
                  }}>
                    <Typography sx={{ fontWeight: 600, textAlign: 'center', fontSize: 14 }}>{item.export}</Typography>
                    <Typography sx={{ fontWeight: 500, textAlign: 'center', fontSize: 14 }}>{item.number}</Typography>
                  </Box>
                ))}
                {/* 합계 행 */}
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: '80px 1fr',
                  gap: 1,
                  p: 1,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 1,
                  mt: 1,
                  border: '1px solid #e0e0e0'
                }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 14, textAlign: 'center', color: '#333' }}>{exportSum}</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: 14, textAlign: 'center', color: '#666' }}>-</Typography>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, minWidth: 320 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2', mr: 1 }}>
                내수
              </Typography>
            </Box>
            {summaryLoading ? (
                              <CircularProgress size={20} sx={{ color: '#1976d2' }} />
            ) : summaryError ? (
              <Typography color="error">{summaryError}</Typography>
            ) : filteredDomestic.length === 0 ? (
              <Typography color="text.secondary">내수/출고/차량번호 데이터가 없습니다.</Typography>
            ) : (
              <>
                {/* 테이블 헤더 */}
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: '60px 60px 1fr',
                  gap: 1,
                  p: 1,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 1,
                  mb: 1,
                  border: '1px solid #e0e0e0'
                }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 14, textAlign: 'center' }}>내수</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: 14, textAlign: 'center' }}>출고</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: 14, textAlign: 'center' }}>차량번호</Typography>
                </Box>
                {/* 테이블 데이터 */}
                {filteredDomestic.map((item, idx) => (
                  <Box key={idx} sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: '60px 60px 1fr',
                    gap: 1,
                    p: 1,
                    borderBottom: idx === filteredDomestic.length - 1 ? 'none' : '1px solid #e0e0e0',
                    '&:hover': { backgroundColor: '#f8f9fa' }
                  }}>
                    <Typography sx={{ fontWeight: 600, textAlign: 'center', fontSize: 14 }}>{item.domestic}</Typography>
                    <Typography sx={{ fontWeight: 600, textAlign: 'center', fontSize: 14 }}>{item.shipment}</Typography>
                    <Typography sx={{ fontWeight: 500, textAlign: 'center', fontSize: 14 }}>{item.number}</Typography>
                  </Box>
                ))}
                {/* 합계 행 */}
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: '60px 60px 1fr',
                  gap: 1,
                  p: 1,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 1,
                  mt: 1,
                  border: '1px solid #e0e0e0'
                }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 14, textAlign: 'center', color: '#333' }}>{domesticSum}</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: 14, textAlign: 'center', color: '#333' }}>{shipmentSum}</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: 14, textAlign: 'center', color: '#666' }}>-</Typography>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  };

  // 렌더링 분기
  if (isOttogi && viewType === 'summary') {
    return renderSummaryCards();
  }
  // 나머지: 기존 표/카드
  return (
    <Box
      ref={containerRef}
      className={isMobile ? 'dispatch-container' : ''}
      sx={{ 
        width: '100%', 
        height: '100%',
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        ...(isMobile && {
          background: '#ffffff',
          color: '#333333',
          px: 0,
          pb: 12,
          flex: 1,
          minHeight: 0,
          touchAction: 'pan-y',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain'
        })
      }}
    >
      {loading ? (
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          ...(isMobile && {
            background: '#ffffff'
          })
        }}>
                     <CircularProgress sx={{ color: '#1976d2' }} />
        </Box>
      ) : shouldShowNoData ? (
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          alignItems: 'flex-start', 
          justifyContent: 'center',
          pt: isMobile ? 4 : 8,
          ...(isMobile && {
            background: '#ffffff',
            px: 0
          })
        }}>
          <Card sx={{
            bgcolor: isMobile ? '#f8f9fa' : '#f8fafb',
            borderRadius: 3,
            boxShadow: isMobile ? '0 2px 8px rgba(0, 0, 0, 0.1)' : '0 2px 12px rgba(0,0,0,0.08)',
            p: 2,
            width: '100%',
            maxWidth: isMobile ? '100%' : '500px',
            minHeight: '80px',
            boxSizing: 'border-box',
            border: isMobile ? '1px solid #e9ecef' : '1px solid #f0f0f0',
            mx: isMobile ? 2 : 0
          }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography
                color={isMobile ? "#333333" : "text.secondary"}
                align="center"
                sx={{ 
                  fontSize: isMobile ? 16 : 18,
                  fontWeight: 600,
                  lineHeight: 1.4,
                  wordBreak: 'keep-all'
                }}
              >
                해당 날짜에 배차 내역이 존재하지 않습니다.
              </Typography>
            </CardContent>
          </Card>
        </Box>
      ) : (
        viewMode === 'table' ? renderTableView() : renderCardView()
      )}
      {/* 배차완료 알림 스낵바 */}
      <Snackbar
        open={dispatchNotification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseNotification} severity={dispatchNotification.severity} sx={{ width: '100%' }}>
          {dispatchNotification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerDispatchReportViewer; 